import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Play, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { videos } from "@/content/site";
import { Reveal, SectionHead } from "@/components/apex/primitives";
import MediaViewer from "@/components/apex/MediaViewer";

const VideoShowcase = () => {
  const { t, isRtl } = useLang();
  const [active, setActive] = useState(null);
  const main = videos[0];

  return (
    <section id="film" data-testid="video-section" className="relative py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead align="center" label={t.video.label} title={t.video.title} body={t.video.body} />

        <Reveal delay={0.1}>
          <div className="group relative mt-10 overflow-hidden rounded-[2rem] bg-[var(--charcoal)]" data-testid="video-banner">
            <img
              src={main.poster}
              alt={isRtl ? main.alt_ar : main.alt_en}
              loading="lazy"
              decoding="async"
              className="h-[420px] w-full object-cover opacity-70 blur-[2px] transition-transform duration-[1500ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105 sm:h-[560px]"
              style={{ objectPosition: "center 35%" }}
            />
            <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(21,21,21,0.75),rgba(21,21,21,0.15))]" aria-hidden="true" />

            {main.src ? (
              <button
                type="button"
                onClick={() => setActive(main)}
                data-testid={`video-play-${main.id}`}
                aria-label={`${t.video.play}: ${isRtl ? main.title_ar : main.title_en}`}
                className="absolute inset-0 z-10 grid place-items-center"
              >
                <span className="flex flex-col items-center gap-4">
                  <span className="relative grid h-24 w-24 place-items-center rounded-full bg-[var(--yellow)] text-[var(--charcoal)] shadow-[0_30px_60px_-20px_rgba(245,197,24,0.55)] transition-transform duration-500 group-hover:scale-110">
                    <span className="absolute inset-0 animate-ping rounded-full bg-[var(--yellow)]/40" aria-hidden="true" />
                    <Play className="relative h-7 w-7 translate-x-[2px] fill-current rtl:-translate-x-[2px] rtl:scale-x-[-1]" />
                  </span>
                  <span className="text-[15px] font-semibold text-white">{t.video.play}</span>
                </span>
              </button>
            ) : (
              <a
                href={main.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`video-external-${main.id}`}
                className="absolute inset-0 z-10 grid place-items-center"
              >
                <span className="ap-btn">
                  <ExternalLink className="h-4 w-4" strokeWidth={2} />
                  {t.video.external}
                </span>
              </a>
            )}

            <p className="absolute bottom-6 start-6 z-10 rounded-full bg-white/15 px-5 py-2.5 text-[14px] font-medium text-white backdrop-blur-md sm:bottom-8 sm:start-8">
              {isRtl ? main.title_ar : main.title_en}
            </p>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {active && <MediaViewer item={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </section>
  );
};

export default VideoShowcase;
